import BigNumber from 'bignumber.js'
import { getAddress } from 'utils/addressHelpers'
import { getCrc20Contract, getRewarderContract, getRewarderV2Contract } from 'utils/contractHelpers'
import { MulticallV2Encoded, MultiEncodedcallsback } from 'utils/multicall'
import { BIG_TEN } from 'utils/bigNumber'
import { ONE_DAY_UNIX } from 'config/constants/info'
import type { SerializedFarmConfig } from 'config/constants/types'
import type { AwaitedReturnType } from 'utils/types'
import type { SerializedFarmData } from 'state/types'
import type { Erc20, Craftsman, CraftsmanV2, Rewarder, RewarderV2 } from '../../../types/ethers-contracts'

interface FetchFarmWithMulticallsbackParams {
  craftsmanContract: Craftsman
  craftsmanV2Contract: CraftsmanV2
  totalAllocPoint: BigNumber
}

const fetchFarmWithMulticallsback = (
  farmConfig: SerializedFarmConfig,
  params: FetchFarmWithMulticallsbackParams,
): MultiEncodedcallsback => {
  if (Number.isInteger(farmConfig.pid)) {
    if (farmConfig.version === 1) {
      return fetchFarmV1(farmConfig, params)
    }
    if (farmConfig.version === 2) {
      return fetchFarmV2(farmConfig, params)
    }
  }

  return fetchFarmBase(farmConfig, params)
}

export default fetchFarmWithMulticallsback

const fetchFarmBase = (
  farmConfig: SerializedFarmConfig,
  params: FetchFarmWithMulticallsbackParams,
): MultiEncodedcallsback<SerializedFarmData> => {
  const { lpAddresses, token, quoteToken } = farmConfig
  const lpAddress = getAddress(lpAddresses)

  const tokenContract = getCrc20Contract(token.address)
  const quoteTokenContract = getCrc20Contract(quoteToken.address)
  const lpTokenContract = getCrc20Contract(lpAddress)

  return {
    calls: [
      [tokenContract, 'balanceOf', tokenContract.interface.encodeFunctionData('balanceOf', [lpAddress])],
      [quoteTokenContract, 'balanceOf', quoteTokenContract.interface.encodeFunctionData('balanceOf', [lpAddress])],
      [
        lpTokenContract,
        'balanceOf',
        tokenContract.interface.encodeFunctionData('balanceOf', [params.craftsmanContract.address]),
      ],
      [lpTokenContract, 'totalSupply', tokenContract.interface.encodeFunctionData('totalSupply')],
      [tokenContract, 'decimals', tokenContract.interface.encodeFunctionData('decimals')],
      [quoteTokenContract, 'decimals', quoteTokenContract.interface.encodeFunctionData('decimals')],
    ],
    callback: (responses) => {
      const [tokenBalanceLP, quoteTokenBalanceLP, lpTokenBalanceMC, lpTotalSupply, tokenDecimals, quoteTokenDecimals] =
        responses as [
          AwaitedReturnType<Erc20['balanceOf']>,
          AwaitedReturnType<Erc20['balanceOf']>,
          AwaitedReturnType<Erc20['balanceOf']>,
          AwaitedReturnType<Erc20['totalSupply']>,
          AwaitedReturnType<Erc20['decimals']>,
          AwaitedReturnType<Erc20['decimals']>,
        ]

      // Ratio in % of LP tokens that are staked in the MC, vs the total number in circulation
      const lpTokenRatio = new BigNumber(lpTokenBalanceMC.toString()).div(new BigNumber(lpTotalSupply.toString()))
      // const lpTokenRatio = new BigNumber(1000000).div(new BigNumber(1000)) // FIXME debug hard code for farms

      // Raw amount of token in the LP, including those not staked
      const tokenAmountTotal = new BigNumber(tokenBalanceLP.toString()).div(BIG_TEN.pow(tokenDecimals))
      const quoteTokenAmountTotal = new BigNumber(quoteTokenBalanceLP.toString()).div(BIG_TEN.pow(quoteTokenDecimals))

      // Amount of quoteToken in the LP that are staked in the MC
      const quoteTokenAmountMc = quoteTokenAmountTotal.times(lpTokenRatio)

      // Total staked in LP, in quote token value
      const lpTotalInQuoteToken = quoteTokenAmountMc.times(new BigNumber(2))

      return {
        tokenAmountTotal: tokenAmountTotal.toJSON(),
        lpTotalSupply: new BigNumber(lpTotalSupply.toString()).toJSON(),
        lpTotalInQuoteToken: lpTotalInQuoteToken.toJSON(),
        tokenPriceVsQuote: quoteTokenAmountTotal.div(tokenAmountTotal).toJSON(),
        poolWeight: '0',
        allocPoint: '0',
        multiplier: '0X',
        lpTokenBalanceMC: new BigNumber(lpTokenBalanceMC.toString()).toJSON(),
        rewardData: null,
        lpTokenAmountInCraftsmanV2: '0',
      }
    },
  }
}

const fetchFarmV1 = (
  farmConfig: SerializedFarmConfig,
  params: FetchFarmWithMulticallsbackParams,
): MultiEncodedcallsback<SerializedFarmData> => {
  const { craftsmanContract, totalAllocPoint } = params
  const { calls: baseCalls, callback: baseCallback } = fetchFarmBase(farmConfig, params)
  const { pid } = farmConfig

  return {
    calls: [
      ...baseCalls,
      [craftsmanContract, 'poolInfo', craftsmanContract.interface.encodeFunctionData('poolInfo', [pid])],
    ],
    callback: (responses) => {
      const farmBaseData = baseCallback(responses)
      const [poolInfo] = responses.slice(baseCalls.length) as [AwaitedReturnType<Craftsman['poolInfo']>]

      const allocPoint = new BigNumber(poolInfo.allocPoint?._hex)
      const poolWeight = allocPoint.div(totalAllocPoint)
      return {
        ...farmBaseData,
        poolWeight: poolWeight.toJSON(),
        allocPoint: allocPoint.toJSON(),
        multiplier: `${allocPoint.div(100).toString()}X`,
      }
    },
  }
}

const fetchFarmV2 = (
  farmConfig: SerializedFarmConfig,
  params: FetchFarmWithMulticallsbackParams,
): MultiEncodedcallsback<SerializedFarmData> => {
  const { craftsmanContract, craftsmanV2Contract } = params
  const { calls: v1Calls, callback: v1Callback } = fetchFarmV1(farmConfig, params)
  const { pid } = farmConfig

  const rewarderConfigs = farmConfig.rewarders ?? []
  const rewarderCalls = rewarderConfigs.flatMap<MulticallV2Encoded>((rewarderConfig) => {
    const rewarderAddr: string = getAddress(rewarderConfig.address)
    const rewarderContract = rewarderConfig.nft
      ? getRewarderV2Contract(rewarderAddr)
      : getRewarderContract(rewarderAddr)

    return [
      [rewarderContract, 'poolInfo', rewarderContract.interface.encodeFunctionData('poolInfo', [pid])],
      [rewarderContract, 'totalAllocPoint', rewarderContract.interface.encodeFunctionData('totalAllocPoint')],
      [rewarderContract, 'rewardPerSecond', rewarderContract.interface.encodeFunctionData('rewardPerSecond')],
      [rewarderContract, 'rewardStartTimestamp', rewarderContract.interface.encodeFunctionData('rewardStartTimestamp')],
      [rewarderContract, 'rewardEndTimestamp', rewarderContract.interface.encodeFunctionData('rewardEndTimestamp')],
    ]
  })
  const callsPerRewarderConfg = rewarderCalls.length / rewarderConfigs.length

  return {
    calls: [
      ...v1Calls,
      [
        craftsmanContract,
        'userInfo',
        craftsmanContract.interface.encodeFunctionData('userInfo', [pid, craftsmanV2Contract.address]),
      ],
      ...rewarderCalls,
    ],
    callback: (responses) => {
      const farmV1Data = v1Callback(responses)
      const [craftsmanV2StakingInfo, ...rewarderResponses] = responses.slice(v1Calls.length) as [
        AwaitedReturnType<Craftsman['userInfo']>,
        ...any[]
      ]

      const rewardData = rewarderConfigs.map((rewarderConfig, i) => {
        const [rewardAllocPoint, rewardTotalAllocPoint, rewardPerSecond, rewardStartTimestamp, rewardEndTimestamp] =
          rewarderResponses.slice(callsPerRewarderConfg * i) as [
            AwaitedReturnType<(Rewarder & RewarderV2)['poolInfo']>,
            AwaitedReturnType<(Rewarder & RewarderV2)['totalAllocPoint']>,
            AwaitedReturnType<(Rewarder & RewarderV2)['rewardPerSecond']>,
            AwaitedReturnType<(Rewarder & RewarderV2)['rewardStartTimestamp']>,
            AwaitedReturnType<(Rewarder & RewarderV2)['rewardEndTimestamp']>,
          ]

        const rewarderAddr: string = getAddress(rewarderConfig.address)

        return {
          allocPoint: new BigNumber(rewardAllocPoint.allocPoint?._hex).toJSON(),
          totalAllocPoint: new BigNumber(rewardTotalAllocPoint.toString()).toJSON(),
          address: rewarderAddr,
          token: rewarderConfig.token,
          totalRewardPerSecond: new BigNumber(rewardPerSecond.toString()).toJSON(),
          rewardPerDay: new BigNumber(rewardPerSecond.toString())
            .times(ONE_DAY_UNIX)
            .times(rewardAllocPoint.allocPoint?._hex)
            .div(rewardTotalAllocPoint.toString())
            .toJSON(),
          rewardStartTimestamp: new BigNumber(rewardStartTimestamp.toString()).toJSON(),
          rewardEndTimestamp: new BigNumber(rewardEndTimestamp.toString()).toJSON(),
        }
      })

      const now = Date.now() / 1000
      const multiYieldLabel =
        farmConfig.multiYieldLabel ||
        [
          parseInt(farmV1Data.allocPoint) > 0,
          ...rewardData.map((data) => {
            return (
              parseInt(data.rewardStartTimestamp) <= now &&
              now <= parseInt(data.rewardEndTimestamp) &&
              parseInt(data.rewardPerDay) > 0
            )
          }),
        ].filter((hasReward) => hasReward).length >= 2

      return {
        ...farmV1Data,
        lpTokenAmountInCraftsmanV2: new BigNumber(craftsmanV2StakingInfo.amount._hex).toJSON(),
        rewardData,
        multiYieldLabel,
      }
    },
  }
}
